/* =========================================================================
   LandingPage9 — the next landing revision, forked from LandingPage7.
   LandingPage7 and 8 are UNTOUCHED; this page carries its own lp9-prefixed
   stylesheet so it can diverge freely.

   Structure is still 7's Hormozi flow: hero → the problem → how it works →
   cost comparison + offer → guarantee → final CTA.

   HERO reads its own lp9.* copy (EN + ES) and is the one part that has
   diverged so far. It merges what worked in each predecessor:
     • from 8 — the day-one win ("your app running today", proof in lesson 1)
     • from 7 — "built by you with AI", the student as the builder
   and drops the 60-day deadline both pages made in the hero, because how
   long a student takes to finish the course isn't ours to promise. "Go at
   your own pace" replaces it. The deadline claim elsewhere on the page is
   now 30 days, on LP9-only overrides — lp9.how4P, lp9.anchorYouNote and
   the refund promise in lp9.guaranteeBody. Their lp6.* originals still say
   60 days and are left alone, because LandingPage6/7 share them.

   ENGLISH-ONLY BILINGUAL FRAME. The Spanish wedge is "you don't need
   English" — which says nothing to someone already reading in English, and
   worse, reads as "this course is Spanish-only". So the EN page instead sells
   the course as bilingual, and names the real wall for an English-speaking
   non-coder: jargon, not English. Every one of those strings is an EN-ONLY
   key in en/common.json (lp9.problem3T/3P, how1P, how3P, anchorCollegeNote,
   finalBody, stackItem1/2, plus the eyebrow/heroLead/anchorYouNote edits).
   es/common.json is deliberately untouched — reads below go through
   t(["lp9.x", "lp6.x"]), which takes the first key that EXISTS, so Spanish
   finds no lp9 override and falls through to the shared lp6 copy unchanged.
   Add a future LP9-only override by adding the lp9 key alone; no code change.

   Everything else still reads the shared lp6.* copy.
   Everything is scoped under .lp9 so it never collides with the Argon
   globals or the shelved LandingPage5.
   ========================================================================= */

import React, { useEffect } from "react";

// reactstrap components
import { Container, Row, Col } from "reactstrap";

import "./LandingPage9.css";

// icons
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faLaptopCode,
  faDollarSign,
  faLanguage,
  faUsers,
  faLightbulb,
  faRobot,
  faHeadphones,
  faRocket,
  faCheck,
  faShieldHalved,
  faClock,
  faGraduationCap,
  faUserTie,
  faInfinity,
  faXmark,
} from "@fortawesome/free-solid-svg-icons";

// core components
import DemoNavbar from "components/Navbars/DemoNavbar.js";
import MainFooter from "components/Footers/MainFooter.js";
import ValueStack, { SHOW_STACK_PRICES } from "components/Offer/ValueStack.js";
import { ENROLLMENT_OPEN } from "../../config/enrollment";

import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

// The fears that keep the idea stuck (Hormozi step 2: list the problems).
const PROBLEMS = [
  { n: 1, icon: faLaptopCode, tone: "lp9-ic-cyan" },
  { n: 2, icon: faDollarSign, tone: "lp9-ic-purple" },
  { n: 3, icon: faLanguage, tone: "lp9-ic-blue" },
  { n: 4, icon: faUsers, tone: "lp9-ic-cyan" },
];

// How it works — the believable path to the dream outcome.
const STEPS = [
  { n: 1, icon: faLightbulb, tone: "lp9-ic-cyan" },
  { n: 2, icon: faRobot, tone: "lp9-ic-purple" },
  { n: 3, icon: faHeadphones, tone: "lp9-ic-blue" },
  { n: 4, icon: faRocket, tone: "lp9-ic-cyan" },
];

const LandingPage9 = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  // Transparent navbar over the hero, fading to solid navy on scroll. Body
  // classes are cleaned up on unmount so every other page keeps the solid one.
  useEffect(() => {
    document.body.classList.add("lp9-active");
    const onScroll = () => {
      document.body.classList.toggle("lp9-nav-solid", window.scrollY > 40);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      document.body.classList.remove("lp9-active", "lp9-nav-solid");
      window.removeEventListener("scroll", onScroll);
    };
  }, []);

  const goEnroll = () => navigate("/subscriptions");
  const scrollToOffer = () => {
    const el = document.getElementById("lp9-offer");
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <>
      <DemoNavbar />
      <main className="lp9">
        {/* ----------------------------- HERO ----------------------------- */}
        <section className="lp9-hero">
          <span className="lp9-orb lp9-orb-1" />
          <span className="lp9-orb lp9-orb-2" />
          <span className="lp9-orb lp9-orb-3" />

          <Container>
            <Row>
              <Col lg="7">
                <div className="lp9-glass">
                  <span className="lp9-eyebrow">
                    <span className="dot" />
                    {t("lp9.eyebrow")}
                  </span>
                  <h1 className="lp9-title">{t("lp9.heroTitle")}</h1>
                  <p className="lp9-lead">{t("lp9.heroLead")}</p>

                  <div className="lp9-actions">
                    <button className="lp9-btn-primary" onClick={goEnroll}>
                      {t("lp9.heroPrimary")}
                    </button>
                    <button className="lp9-btn-ghost" onClick={scrollToOffer}>
                      {t("lp9.heroSecondary")}
                    </button>
                  </div>

                  <div className="lp9-pills">
                    <span className="lp9-pill">{t("lp9.pill1")}</span>
                    <span className="lp9-pill">{t("lp9.pill2")}</span>
                    <span className="lp9-pill">{t("lp9.pill3")}</span>
                  </div>
                </div>
              </Col>

              <Col lg="5">
                <div className="lp9-preview-wrap">
                  <div className="lp9-preview">
                    <div className="lp9-dots">
                      <span />
                      <span />
                      <span />
                    </div>
                    <div className="lp9-code">
                      <div className="lp9-code-line">
                        <div className="lp9-bar cyan" style={{ width: "34%" }} />
                        <div className="lp9-bar mute" style={{ width: "24%" }} />
                      </div>
                      <div className="lp9-code-line">
                        <div className="lp9-bar purple" style={{ width: "20%" }} />
                        <div className="lp9-bar mute" style={{ width: "44%" }} />
                      </div>
                      <div className="lp9-code-line">
                        <div className="lp9-bar dim" style={{ width: "56%" }} />
                      </div>
                    </div>
                    <div className="lp9-bubble">
                      <div className="lp9-avatar" />
                      <div className="lp9-bubble-lines">
                        <div className="lp9-bar" style={{ width: "90%" }} />
                        <div className="lp9-bar" style={{ width: "68%" }} />
                      </div>
                    </div>
                    <span className="lp9-ask">{t("lp6.previewAsk")}</span>
                  </div>
                </div>
              </Col>
            </Row>
          </Container>
        </section>

        {/* --------------------------- PROBLEM ---------------------------- */}
        <section className="lp9-problem">
          <Container>
            <div className="lp9-section-head lp9-head-dark">
              <h2>{t("lp6.problemTitle")}</h2>
              <p>{t("lp6.problemSubtitle")}</p>
            </div>
            <Row>
              {PROBLEMS.map((p) => (
                <Col lg="3" md="6" className="lp9-col" key={p.n}>
                  <div className="lp9-card lp9-card-dark">
                    <div className={`lp9-card-icon ${p.tone}`}>
                      <FontAwesomeIcon icon={p.icon} />
                    </div>
                    <h6>{t([`lp9.problem${p.n}T`, `lp6.problem${p.n}T`])}</h6>
                    <p>{t([`lp9.problem${p.n}P`, `lp6.problem${p.n}P`])}</p>
                  </div>
                </Col>
              ))}
            </Row>
          </Container>
        </section>

        {/* ------------------------- HOW IT WORKS ------------------------- */}
        <section className="lp9-how">
          <Container>
            <div className="lp9-section-head">
              <h2>{t("lp6.howTitle")}</h2>
              <p>{t("lp6.howSubtitle")}</p>
            </div>
            <Row>
              {STEPS.map((s, i) => (
                <Col lg="3" md="6" className="lp9-col" key={s.n}>
                  <div className="lp9-step">
                    <span className="lp9-step-num">{i + 1}</span>
                    <div className={`lp9-card-icon ${s.tone}`}>
                      <FontAwesomeIcon icon={s.icon} />
                    </div>
                    <h6>{t([`lp9.how${s.n}T`, `lp6.how${s.n}T`])}</h6>
                    <p>{t([`lp9.how${s.n}P`, `lp6.how${s.n}P`])}</p>
                  </div>
                </Col>
              ))}
            </Row>
          </Container>
        </section>

        {/* --------------------------- THE ANCHOR ------------------------- */}
        {/* Hormozi price anchor: frame the cost of the real alternatives
            (college / hiring an engineer) right before the offer, so the
            $497 lands soft. */}
        <section className="lp9-anchor">
          <Container>
            <div className="lp9-section-head lp9-head-dark">
              <span className="lp9-eyebrow-2 lp9-eyebrow-light">
                {t("lp6.anchorEyebrow")}
              </span>
              <h2>{t("lp6.anchorTitle")}</h2>
              <p>{t("lp6.anchorSubtitle")}</p>
            </div>

            <Row className="lp9-anchor-grid">
              <Col lg="4" md="6" className="lp9-col">
                <div className="lp9-anchor-card">
                  <span className="lp9-anchor-mark cross">
                    <FontAwesomeIcon icon={faXmark} />
                  </span>
                  <div className="lp9-anchor-ic">
                    <FontAwesomeIcon icon={faGraduationCap} />
                  </div>
                  <h6>{t("lp6.anchorCollegeLabel")}</h6>
                  <div className="lp9-anchor-cost">
                    {t("lp6.anchorCollegeCost")}
                  </div>
                  <p>{t(["lp9.anchorCollegeNote", "lp6.anchorCollegeNote"])}</p>
                </div>
              </Col>

              <Col lg="4" md="6" className="lp9-col">
                <div className="lp9-anchor-card">
                  <span className="lp9-anchor-mark cross">
                    <FontAwesomeIcon icon={faXmark} />
                  </span>
                  <div className="lp9-anchor-ic">
                    <FontAwesomeIcon icon={faUserTie} />
                  </div>
                  <h6>{t("lp6.anchorEngineerLabel")}</h6>
                  <div className="lp9-anchor-cost">
                    {t("lp6.anchorEngineerCost")}
                  </div>
                  <p>{t("lp6.anchorEngineerNote")}</p>
                </div>
              </Col>

              <Col lg="4" md="6" className="lp9-col">
                <div className="lp9-anchor-card win">
                  <span className="lp9-anchor-mark check">
                    <FontAwesomeIcon icon={faCheck} />
                  </span>
                  <div className="lp9-anchor-ic">
                    <FontAwesomeIcon icon={faInfinity} />
                  </div>
                  <h6>{t("lp6.anchorYouLabel")}</h6>
                  <div className="lp9-anchor-cost">
                    {t("lp6.anchorYouCost")}
                  </div>
                  <p>{t("lp9.anchorYouNote")}</p>
                </div>
              </Col>
            </Row>

            <p className="lp9-anchor-punch">{t("lp6.anchorPunch")}</p>
          </Container>
        </section>

        {/* --------------------- OFFER / WHAT'S INCLUDED ------------------- */}
        <section className="lp9-offer" id="lp9-offer">
          <Container>
            <div className="lp9-section-head">
              <span className="lp9-eyebrow-2">{t("lp6.stackEyebrow")}</span>
              <h2>{t("lp6.stackTitle")}</h2>
              <p>{t("lp6.stackSubtitle")}</p>
            </div>

            <Row className="lp9-offer-grid">
              <ValueStack prefix="lp9" />

              <Col lg="5">
                <div className="lp9-price-card">
                  <span className="lp9-price-eyebrow">
                    {t("lp6.priceEyebrow")}
                  </span>
                  <h3 className="lp9-price-title">{t("lp6.priceTitle")}</h3>
                  <p className="lp9-price-sub">{t("lp6.priceSubtitle")}</p>

                  <div className="lp9-price-amount">
                    {/* The struck-through price is the value-stack total, so it
                        only makes sense while the stack is on the page. With the
                        stack shelved, the comparison line below carries the
                        anchor instead — using a real cost, not a self-assigned
                        one. */}
                    {SHOW_STACK_PRICES && (
                      <span className="lp9-price-strike">
                        {t("lp6.stackTotalValue")}
                      </span>
                    )}
                    <span className="lp9-price-now">{t("lp6.priceAmount")}</span>
                  </div>
                  <span className="lp9-price-plan">{t("lp6.pricePlan")}</span>
                  {!SHOW_STACK_PRICES && (
                    <span className="lp9-price-compare">
                      {t("lp6.priceCompare")}
                    </span>
                  )}

                  <button
                    className="lp9-btn-primary lp9-price-btn"
                    onClick={goEnroll}
                  >
                    {/* Don't promise a seat we can't sell yet — while enrollment
                        is closed this reads "Coming soon" and the offer page it
                        opens explains why. */}
                    {ENROLLMENT_OPEN
                      ? t("lp6.priceCta")
                      : t("enrollment.comingSoonCta")}
                  </button>

                  <div className="lp9-price-meta">
                    <span>
                      <FontAwesomeIcon icon={faUsers} /> {t("lp6.priceSeats")}
                    </span>
                    <span>
                      <FontAwesomeIcon icon={faClock} /> {t("lp6.priceStart")}
                    </span>
                  </div>

                  <div className="lp9-price-guarantee">
                    <FontAwesomeIcon icon={faShieldHalved} />
                    <span>{t("lp6.guaranteeTitle")}</span>
                  </div>
                </div>
              </Col>
            </Row>
          </Container>
        </section>

        {/* --------------------------- GUARANTEE -------------------------- */}
        <section className="lp9-guarantee">
          <Container>
            <div className="lp9-guarantee-card">
              <div className="lp9-guarantee-badge">
                <FontAwesomeIcon icon={faShieldHalved} />
              </div>
              <div>
                <span className="lp9-eyebrow-2">
                  {t("lp6.guaranteeEyebrow")}
                </span>
                <h3>{t("lp6.guaranteeTitle")}</h3>
                <p>{t("lp9.guaranteeBody")}</p>
              </div>
            </div>
          </Container>
        </section>

        {/* --------------------------- FINAL CTA -------------------------- */}
        <section className="lp9-cta">
          <Container>
            <div className="lp9-cta-card">
              <Row>
                <Col lg="8">
                  <h3>{t("lp6.finalTitle")}</h3>
                  <p>{t(["lp9.finalBody", "lp6.finalBody"])}</p>
                </Col>
                <Col lg="4" className="ml-lg-auto">
                  <button className="lp9-cta-btn" onClick={goEnroll}>
                    {t("lp6.finalCta")}
                  </button>
                </Col>
              </Row>
            </div>
          </Container>
        </section>
      </main>
      <MainFooter />
    </>
  );
};

export default LandingPage9;
